import type * as mapboxgl from 'mapbox-gl'
import { DisplayModeOption } from '../interfaces/display-mode-option.interface'

/**
 * Native Mapbox control rendering a segmented two-option toggle
 * (e.g. crops / fields). Rendered as a `mapboxgl-ctrl-group` so it blends
 * with the other native controls, with the active option highlighted.
 */
export class DisplayModeControl implements mapboxgl.IControl {
  // Created by `onAdd`, which Mapbox calls synchronously from `map.addControl`
  private _container!: HTMLDivElement
  private readonly _buttons = new Map<string, HTMLButtonElement>()
  private _currentId: string

  constructor(
    private _options: DisplayModeOption[],
    initialId: string,
    private readonly _onChange: (id: string) => void,
  ) {
    this._currentId = initialId
  }

  onAdd(): HTMLElement {
    this._container = document.createElement('div')
    this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group'
    this._container.style.display = 'flex'

    for (const option of this._options) {
      const button = document.createElement('button')
      button.type = 'button'
      button.textContent = option.label
      button.setAttribute('aria-label', option.label)
      button.setAttribute('data-testid', `map-${option.id}-btn`)
      button.style.width = 'auto'
      button.style.padding = '0 12px'
      button.style.fontSize = '13px'
      button.style.fontWeight = '500'
      button.style.lineHeight = '29px'
      button.style.whiteSpace = 'nowrap'
      button.addEventListener('click', () => this._select(option.id))
      this._buttons.set(option.id, button)
      this._container.appendChild(button)
    }

    this._applyActive()
    return this._container
  }

  onRemove(): void {
    if (this._container?.parentNode) {
      this._container.parentNode.removeChild(this._container)
    }
    this._buttons.clear()
  }

  /** Updates the active option without triggering the callback (external sync). */
  setActive(id: string): void {
    if (this._currentId === id) {
      return
    }
    this._currentId = id
    this._applyActive()
  }

  /** Updates the labels (e.g. after a language change). */
  setLabels(labels: Record<string, string>): void {
    this._options = this._options.map((option) => ({ ...option, label: labels[option.id] ?? option.label }))
    this._options.forEach((option) => {
      const button = this._buttons.get(option.id)
      if (button) {
        button.textContent = option.label
        button.setAttribute('aria-label', option.label)
      }
    })
  }

  private _select(id: string): void {
    if (this._currentId === id) {
      return
    }
    this._currentId = id
    this._applyActive()
    this._onChange(id)
  }

  private _applyActive(): void {
    this._buttons.forEach((button, id) => {
      const active = id === this._currentId
      button.style.backgroundColor = active ? 'var(--dark-green)' : ''
      button.style.color = active ? 'var(--white)' : 'var(--dark-grey)'
      button.setAttribute('aria-pressed', String(active))
    })
  }
}
